import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { apiErrorMessage, useAuth } from "@/AuthContext";
import { LOGOUT } from "@/constants/testIds/auth";
import AdminNav from "@/pages/AdminNav";

function Stat({ label, value }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </article>
  );
}

export default function AdminCrmBackfillPage() {
  const { logout } = useAuth();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  async function runDry() {
    setBusy("dry");
    setError("");
    try {
      const res = await api.get("/admin/crm/backfill");
      setReport(res.data || null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  async function runReal() {
    setBusy("real");
    setError("");
    try {
      const res = await api.post("/admin/crm/backfill", { confirm: true });
      setReport(res.data || null);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy("");
    }
  }

  const skipped = Array.isArray(report?.skipped) ? report.skipped : [];

  return (
    <div className="mp-shell mp-wide">
      <header className="mp-top">
        <div>
          <p className="mp-kicker">MinutePay</p>
          <h1>CRM Backfill</h1>
          <p className="mp-lead" style={{ marginBottom: 0 }}>
            Sync existing MinutePay users into Brevo as contacts. This tool never sends emails.
          </p>
        </div>
        <div className="mp-top-actions">
          <Button variant="outline" asChild>
            <Link to="/admin">Admin</Link>
          </Button>
          <Button data-testid={LOGOUT.button} variant="outline" onClick={logout}>
            Log out
          </Button>
        </div>
      </header>

      <AdminNav />

      <div className="mp-crm-panels">
        <section className="mp-card mp-crm-dry">
          <h2>Dry run</h2>
          <p>Read Mongo and Brevo. Report who would be created or updated. No contacts are written.</p>
          <Button onClick={runDry} disabled={Boolean(busy)}>
            {busy === "dry" ? "Running…" : "Run Dry Run"}
          </Button>
        </section>

        <section className="mp-card mp-crm-real">
          <h2>Real sync</h2>
          <p>
            Upserts Brevo contacts from Mongo. <strong>Sends no emails.</strong> Does not change access, trial,
            billing, or Paddle data.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="mp-crm-real-btn" disabled={Boolean(busy)}>
                {busy === "real" ? "Syncing…" : "Run Real Sync"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Sync contacts to Brevo?</AlertDialogTitle>
                <AlertDialogDescription>
                  This upserts existing MinutePay users into Brevo by email. It sends NO emails. Marketing
                  opt-outs stay opted out. Mongo remains the source of truth.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={runReal}>Sync contacts, send no emails</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </div>

      {error ? <p className="mp-error">{error}</p> : null}

      {report ? (
        <>
          <p className="mp-lead">
            {report.dry_run ? "Dry-run report" : "Real sync report"} · emails sent: {report.emails_sent ?? 0}
            {report.created != null && !report.dry_run ? ` · created ${report.created} · updated ${report.updated} · failed ${report.failed}` : ""}
          </p>
          <section className="mp-counts mp-crm-counts">
            <Stat label="Total Mongo users" value={report.total_mongo_users} />
            <Stat label="Eligible" value={report.eligible} />
            <Stat label="Already in Brevo" value={report.already_in_brevo} />
            <Stat label="New contacts" value={report.new_contacts} />
            <Stat label="Opted out" value={report.opted_out} />
            <Stat label="Skipped" value={skipped.length} />
          </section>

          <div className="mp-table-wrap">
            <table className="mp-table">
              <thead>
                <tr>
                  <th>Reason</th>
                  <th>Email</th>
                  <th>User ID</th>
                </tr>
              </thead>
              <tbody>
                {skipped.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No skipped users</td>
                  </tr>
                ) : (
                  skipped.map((row, index) => (
                    <tr key={`${row.user_id || row.email || "skip"}-${index}`}>
                      <td>{row.reason}</td>
                      <td>{row.email || "—"}</td>
                      <td>{row.user_id || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
